// src/components/Common/FormattedText.jsx
import React from 'react';
import 'katex/dist/katex.min.css';
import Latex from 'react-latex-next';

// --- HELPER FUNCTIONS ---
const escapeRegExp = (string) => {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

const detectOptionPattern = (text) => {
  if (!text) return null;
  const patterns = [
    { start: 'A)', next: 'B)', regex: /([A-E])\)/g },
    { start: 'A.', next: 'B.', regex: /([A-E])\./g },
    { start: '1)', next: '2)', regex: /([1-5])\)/g },
    { start: '1.', next: '2.', regex: /([1-5])\./g },
    { start: 'a)', next: 'b)', regex: /([a-e])\)/g },
    { start: 'a.', next: 'b.', regex: /([a-e])\./g },
    { start: 'I.', next: 'II.', regex: /(I|II|III|IV)\./g }
  ];

  for (let p of patterns) {
    const startPattern = new RegExp(`(^|\\s)${escapeRegExp(p.start)}(\\s|$)`);
    const nextPattern = new RegExp(`(^|\\s)${escapeRegExp(p.next)}(\\s|$)`);
    if (startPattern.test(text) && nextPattern.test(text)) {
      return p.regex;
    }
  }
  return null;
};

// --- COMPONENT ---
const FormattedText = ({ text, className = "" }) => {
  if (!text) return <span className="text-gray-400 italic">No text provided.</span>;

  let formatted = text;
  const activePatternRegex = detectOptionPattern(text);

  // 1. Insert Newline before every option found
  if (activePatternRegex) {
    formatted = text.replace(new RegExp(`(\\s|^)(${activePatternRegex.source})(\\s)`, 'g'), "\n$2$4");
  }

  // 2. Split strictly by Newline first to isolate the options
  const lines = formatted.split('\n');

  return (
    <div className={className}>
      {lines.map((line, i) => {
        const cleanLine = line.trim();
        if (!cleanLine) return null;

        // Check if this specific line is an Option (e.g. starts with "A.")
        let isOption = false;
        if (activePatternRegex) {
          const lineCheck = new RegExp(`^${activePatternRegex.source}`);
          isOption = lineCheck.test(cleanLine);
        }

        // RENDER LOGIC:
        if (isOption) {
          // If it's an option, DO NOT split by full stop. Keep it as one block.
          return (
            <div key={i} className="mb-1 leading-relaxed pl-6 font-semibold">
              <Latex>{cleanLine}</Latex>
            </div>
          );
        } else {
          // If it's normal question text, split sentences for readability
          // Split by ". " followed by a Capital Letter
          const sentences = cleanLine.split(/(?<=\.)\s+(?=[A-Z])/);
          
          return sentences.map((sentence, j) => (
             <div key={`${i}-${j}`} className="mb-1 leading-relaxed text-gray-800">
                <Latex>{sentence}</Latex>
             </div>
          ));
        }
      })}
    </div>
  );
};

export default FormattedText;